import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { FormLayout, FormSection, Select, TextInput } from '~/components';
import { TYPE_CATEGORY_FORM_ID } from '../constants';
import { providerRegistrationView } from '../registration-view.machine';
import {
  RegistrationViewContext,
  TypeAndCategoryValues,
  makeTypeAndCategorySchema,
  selectCategoryOptions,
  selectTypeOptions,
} from '../types';

interface Props {
  actorRef: ActorRefFrom<typeof providerRegistrationView>;
  viewContext: RegistrationViewContext;
}

const TAGS = CT.registration.typeAndCategory;

export const TypeCategoryNPIForm = ({ actorRef, viewContext }: Props) => {
  const categoryOptions = useSelector(viewContext.registrationService, selectCategoryOptions);
  const typeOptions = useSelector(actorRef, selectTypeOptions);

  const loading = useSelector(
    viewContext.registrationService,
    (snapshot) => snapshot?.context.loading
  );
  const typeAndCategoryForm = useSelector(
    actorRef,
    (snapshot) => snapshot.context.typeAndCategoryForm
  );

  // `type` is required only when the selected category has provider types. The
  // zod resolver reads this ref (kept in sync with selectedCategoryHasTypes
  // below) so validation matches the conditionally-rendered Type dropdown: a
  // category with no types stays submittable, one with types requires a choice.
  const categoryHasTypesRef = useRef(false);

  const methods = useForm<TypeAndCategoryValues>({
    defaultValues: typeAndCategoryForm || {
      category: null,
      type: null,
      firstName: '',
      lastName: '',
      npiNum: '',
    },
    resolver: (values, context, options) =>
      zodResolver(makeTypeAndCategorySchema(categoryHasTypesRef.current))(values, context, options),
    mode: 'onBlur',
  });
  const { handleSubmit, watch, setValue } = methods;

  // Watch for category changes to fetch types
  const category = watch('category');

  // Fetch the provider types for the selected category.
  useEffect(() => {
    if (category?.value) {
      actorRef.send({ type: 'ON_SET_CATEGORY_OPTION', id: category.value });
    }
  }, [category]);

  const onSubmitSuccess = (data: TypeAndCategoryValues) => {
    actorRef.send({
      type: 'ON_SUBMIT_TYPE_CATEGORY',
      data,
    });
  };
  const onSubmitError = (err: unknown) => {
    console.log('TypeCategoryNPI Submit Error: ', err);
  };

  // Check if current category has types available
  // typeOptions will contain types for the currently selected category
  const selectedCategoryHasTypes = category != null && typeOptions.length > 0;
  // Keep the resolver's view of "does this category have types" current so
  // `type` is validated as required exactly when the Type dropdown is shown.
  categoryHasTypesRef.current = selectedCategoryHasTypes;

  // Check if current category requires NPI
  const npiRequired = category?.npiFlag;

  const lastNameLabel = () => {
    return npiRequired ? 'Last Name (as it appears on your NPI)' : 'Last Name';
  };
  const firstNameLabel = () => {
    return npiRequired ? 'First Name (as it appears on your NPI)' : 'First Name';
  };

  return (
    <FormProvider {...methods}>
      <form id={TYPE_CATEGORY_FORM_ID} onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          {/* [MCV-1577] One-per-line section rather than a hand-rolled flex
              stack: FormSection drops the `false` a conditional field yields
              instead of leaving a phantom grid cell, so the Type and NPI
              fields can appear and disappear without disturbing the rhythm. */}
          <FormSection componentsPerLine={1}>
            <Select
              name="category"
              label="Provider Category"
              options={categoryOptions}
              onChange={() => {
                // Clear any previously chosen type so a stale type can't be
                // submitted with the new category (or linger against types it
                // no longer belongs to). Fires only on user change, so a
                // pre-filled resume keeps its saved type. [MCV-1486]
                setValue('type', null);
              }}
              noOptionsMessage="No Results"
              isLoading={loading}
              cyTag={TAGS.categoryDropdown}
              errorCyTag={TAGS.categoryError}
              required
            />
            {selectedCategoryHasTypes && (
              <Select
                name="type"
                label="Provider Type"
                options={typeOptions}
                disabled={!category}
                isLoading={loading}
                cyTag={TAGS.typeDropdown}
                errorCyTag={TAGS.typeError}
                required
              />
            )}
            <TextInput
              name="firstName"
              cyTag={TAGS.firstName}
              errorCyTag={TAGS.firstNameError}
              placeholder={firstNameLabel()}
              label="First Name"
              required
            />
            <TextInput
              className="text-md"
              name="lastName"
              cyTag={TAGS.lastName}
              errorCyTag={TAGS.lastNameError}
              placeholder={lastNameLabel()}
              label="Last Name"
              required
            />
            {npiRequired && <TextInput name="npiNum" cyTag={TAGS.npi} label="NPI number" />}
          </FormSection>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
